import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { ArticleCard } from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, getHotArticles } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  const category = categories.find((c) => c.slug === slug);

  const categoryArticles = useMemo(() => {
    if (!category) return [];
    const filtered = getArticlesByCategory(category.id);
    if (sortBy === 'hot') {
      return [...filtered].sort((a, b) => b.viewCount - a.viewCount);
    }
    return [...filtered].sort(
      (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    );
  }, [category, sortBy]);

  const hotArticles = getHotArticles().slice(0, 5);

  if (!category) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900">分类未找到</h2>
          <Link to="/" className="mt-4 text-primary-600 hover:text-primary-700 inline-flex items-center gap-1">
            <i className="ri-arrow-left-line"></i>返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name} - 黑料网${category.description}`}
        description={`黑料网${category.name}频道，${category.description}，汇集最新最全的${category.name}相关爆料资讯，关注黑料网获取持续更新的真实爆料信息。`}
        keywords={`黑料网${category.name},黑料网${category.description},${category.name}最新爆料,黑料网`}
        canonicalUrl={`/category/${category.slug}`}
      />

      {/* Breadcrumb */}
      <div className="pt-20 bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />
        </div>
      </div>

      {/* Category Header */}
      <section className="w-full bg-background-100 px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
              <i className={`${category.icon} text-2xl`}></i>
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                {category.name}
              </h1>
              <p className="text-foreground-600 mt-1">{category.description}</p>
            </div>
          </div>
          <p className="text-sm text-foreground-500">
            共 <strong className="text-foreground-900">{categoryArticles.length}</strong> 篇爆料文章
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Sort Tabs */}
              <div className="flex items-center gap-2 mb-6">
                <button
                  onClick={() => setSortBy('latest')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                    sortBy === 'latest'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  最新发布
                </button>
                <button
                  onClick={() => setSortBy('hot')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                    sortBy === 'hot'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  最多阅读
                </button>
              </div>

              {categoryArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="w-full lg:w-80 flex-shrink-0">
              {/* Category List */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 mb-6">
                <h3 className="font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-stack-line text-primary-500"></i>
                  全部分类
                </h3>
                <div className="space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        cat.slug === slug
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <i className={cat.icon}></i>
                      <span>{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-400">
                        {getArticlesByCategory(cat.id).length}篇
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Hot Articles */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                <h3 className="font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500"></i>
                  热门爆料
                </h3>
                <div className="space-y-4">
                  {hotArticles.map((article, index) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="flex gap-3 group"
                    >
                      <span className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 ${
                        index === 0 ? 'bg-primary-500 text-white' :
                        index === 1 ? 'bg-primary-400 text-white' :
                        index === 2 ? 'bg-primary-300 text-primary-900' :
                        'bg-background-200 text-foreground-500'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <h4 className="text-sm text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                          {article.title}
                        </h4>
                        <span className="text-xs text-foreground-500 mt-1 block">
                          {(article.viewCount / 10000).toFixed(1)}万阅读
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}